import React, { FC } from 'react';
import { CircularProgress } from '@mui/material';
import { Check, Close, DoneAll } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';

import { AnimatedButton } from '../../common';
import { SuggestedBlockActionsVisibility } from './types';
import './SuggestedBlockActionsRow.scss';

// AnimatedButton requires an onPress even when disabled=true blocks it from firing.
const NOOP = () => undefined;

interface SuggestedBlockActionsRowProps {
  visibility: SuggestedBlockActionsVisibility;
  onAccept: () => void;
  onDiscard: () => void;
  acceptLabel: string;
  acceptDisabled?: boolean;
}

/**
 * Per-item Accept/Discard row for the grouped suggested-blocks card — a
 * circular close-icon button (matching ApproveDismissBar's "Dismiss All"
 * button) beside a solid "Accept" pill that fills the remaining width.
 * Replaces AssistantChatSuggestedBlockCTA for the grouped ("screen") card
 * context only.
 */
const SuggestedBlockActionsRow: FC<SuggestedBlockActionsRowProps> = ({
  visibility,
  onAccept,
  onDiscard,
  acceptLabel,
  acceptDisabled = false,
}) => {
  const { t } = useTranslation();

  if (visibility === 'hidden') {
    return null;
  }

  if (visibility === 'loading') {
    return (
      <div className="suggested-block-actions-row__loading-row">
        <CircularProgress size={18} />
      </div>
    );
  }

  // Auto-accepted items were accepted by the user's auto-accept settings, not
  // by a manual click here — shown as a disabled status pill (dimmed, no
  // click handling) with no discard button (unlike a manually-accepted item,
  // this one shouldn't be undone from here).
  if (visibility === 'auto-accepted') {
    return (
      <AnimatedButton
        onPress={NOOP}
        disabled
        className="suggested-block-actions-row__auto-accepted-button"
      >
        <DoneAll fontSize="small" />
        <span className="suggested-block-actions-row__accept-label">
          {t('task.allPlan.planItem.autoAccepted')}
        </span>
      </AnimatedButton>
    );
  }

  return (
    <div className="suggested-block-actions-row">
      <AnimatedButton
        onPress={onDiscard}
        fullWidth={false}
        className="suggested-block-actions-row__discard-button"
      >
        <Close fontSize="small" />
      </AnimatedButton>
      <AnimatedButton
        onPress={onAccept}
        disabled={acceptDisabled}
        wrapperStyle={{ flex: 1 }}
        className="suggested-block-actions-row__accept-button"
      >
        <Check fontSize="small" />
        <span className="suggested-block-actions-row__accept-label">{acceptLabel}</span>
      </AnimatedButton>
    </div>
  );
};

export default SuggestedBlockActionsRow;
